/**
 * Pure reporting maths for the admin Dashboard "Insights" tab.
 *
 * No Supabase / React imports so it can be unit-tested directly
 * (tests/dashboard-insights.spec.ts). Every period is compared with the
 * period of the same length immediately before it.
 */
import { addDays, addMonths, format, parseISO, startOfMonth } from 'date-fns';
import { LEAD_SOURCES, leadSourceOf } from './follow-ups';
import { countsAsLead } from '../../supabase/functions/_shared/enquiry-types';

export type InsightRange = '7d' | '30d' | '90d' | '12m';

export const INSIGHT_RANGES: { value: InsightRange; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
];

export const isInsightRange = (value: unknown): value is InsightRange =>
  INSIGHT_RANGES.some((r) => r.value === value);

export interface InsightBucket {
  /** Axis label. */
  label: string;
  /** Tooltip label for the current bucket. */
  tooltip: string;
  /** Tooltip label for the matching previous-period bucket. */
  prevTooltip: string;
  start: string;
  end: string;
  prevStart: string;
  prevEnd: string;
}

export interface InsightWindow {
  range: InsightRange;
  granularity: 'day' | 'week' | 'month';
  start: string;
  end: string;
  prevStart: string;
  prevEnd: string;
  buckets: InsightBucket[];
}

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');

export function buildInsightWindow(range: InsightRange, today: Date = new Date()): InsightWindow {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (range === '12m') {
    const first = startOfMonth(addMonths(end, -11));
    const buckets: InsightBucket[] = Array.from({ length: 12 }, (_, i) => {
      const s = addMonths(first, i);
      const e = i === 11 ? end : addDays(addMonths(s, 1), -1);
      const ps = addMonths(s, -12);
      // Like-for-like: the current (partial) month is compared with the same days a year earlier.
      const pe = i === 11 ? addMonths(end, -12) : addDays(addMonths(ps, 1), -1);
      return {
        label: format(s, 'MMM'),
        tooltip: format(s, 'MMMM yyyy'),
        prevTooltip: format(ps, 'MMMM yyyy'),
        start: ymd(s),
        end: ymd(e),
        prevStart: ymd(ps),
        prevEnd: ymd(pe),
      };
    });
    return {
      range,
      granularity: 'month',
      start: buckets[0].start,
      end: ymd(end),
      prevStart: buckets[0].prevStart,
      prevEnd: buckets[11].prevEnd,
      buckets,
    };
  }

  const weekly = range === '90d';
  const size = weekly ? 7 : 1;
  const count = range === '7d' ? 7 : range === '30d' ? 30 : 13;
  const span = size * count;
  const start = addDays(end, -(span - 1));
  const buckets: InsightBucket[] = Array.from({ length: count }, (_, i) => {
    const s = addDays(start, i * size);
    const e = addDays(s, size - 1);
    const ps = addDays(s, -span);
    const pe = addDays(e, -span);
    return weekly
      ? {
          label: format(s, 'd MMM'),
          tooltip: `${format(s, 'd MMM')} – ${format(e, 'd MMM')}`,
          prevTooltip: `${format(ps, 'd MMM')} – ${format(pe, 'd MMM')}`,
          start: ymd(s),
          end: ymd(e),
          prevStart: ymd(ps),
          prevEnd: ymd(pe),
        }
      : {
          label: range === '7d' ? format(s, 'EEE') : format(s, 'd MMM'),
          tooltip: format(s, 'EEE d MMM'),
          prevTooltip: format(ps, 'EEE d MMM'),
          start: ymd(s),
          end: ymd(e),
          prevStart: ymd(ps),
          prevEnd: ymd(pe),
        };
  });
  return {
    range,
    granularity: weekly ? 'week' : 'day',
    start: ymd(start),
    end: ymd(end),
    prevStart: buckets[0].prevStart,
    prevEnd: buckets[count - 1].prevEnd,
    buckets,
  };
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface InsightJob {
  id: string;
  scheduled_date: string;
  status: string;
  service_id: string | null;
  team_member_id: string | null;
  customer_id?: string | null;
  client_email?: string | null;
  client_name?: string | null;
}
export interface InsightService {
  id: string;
  name: string;
  /** Already parsed to a number (0 when unknown). */
  price: number;
}
export interface InsightMember {
  id: string;
  name: string;
}
export interface InsightEnquiry {
  id: string;
  status: string;
  created_at: string;
  service_id?: string | null;
  /** website | phone | referral | facebook | google | other; missing = website. */
  lead_source?: string | null;
  /**
   * Contact page messages: what it's about. 'recruitment' and 'partner' aren't
   * sales leads, so they're left out of every enquiry/conversion figure.
   */
  enquiry_type?: string | null;
}
export interface InsightInvoice {
  id: string;
  status: string;
  total_amount: number | null;
  amount_paid: number | null;
  due_date: string | null;
}
export interface InsightDated {
  created_at: string;
}

export interface InsightInput {
  jobs: InsightJob[];
  services: InsightService[];
  members: InsightMember[];
  enquiries: InsightEnquiry[];
  /** Open invoices (any date) — used for the ageing breakdown. */
  invoices: InsightInvoice[];
  customers: InsightDated[];
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

export interface Comparison {
  current: number;
  previous: number;
  /** % change, or null when there is nothing to compare against. */
  change: number | null;
}

export interface InsightSeriesPoint {
  label: string;
  tooltip: string;
  prevTooltip: string;
  revenue: number;
  prevRevenue: number;
  jobs: number;
  prevJobs: number;
}

export interface Insights {
  revenue: Comparison;
  jobs: Comparison;
  completed: Comparison;
  avgJobValue: Comparison;
  newCustomers: Comparison;
  enquiries: Comparison;
  /** Converted ÷ received, as a percentage (0–100). */
  conversion: Comparison;
  /** Customers with more than one booking (in this period, or also booked last period). */
  repeatRate: Comparison;
  series: InsightSeriesPoint[];
  outcomes: { key: 'completed' | 'open' | 'cancelled'; label: string; value: number }[];
  byService: { id: string; name: string; jobs: number; revenue: number }[];
  byWeekday: { day: string; jobs: number }[];
  team: { id: string; name: string; completed: number; jobs: number; revenue: number }[];
  pipeline: {
    total: number;
    stages: { key: string; label: string; value: number }[];
    byService: { id: string; name: string; total: number; counts: Record<string, number> }[];
  };
  outstanding: {
    total: number;
    count: number;
    buckets: { key: string; label: string; amount: number; count: number }[];
  };
  /** Enquiries received this period by where they came from, and how many were booked. */
  bySource: { key: string; label: string; total: number; converted: number; rate: number }[];
}

export const pctChange = (current: number, previous: number): number | null =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

const compare = (current: number, previous: number): Comparison => ({
  current,
  previous,
  change: pctChange(current, previous),
});

const inRange = (d: string, start: string, end: string) => d >= start && d <= end;
/** Timestamps → local calendar day. */
const dayOf = (ts: string) => (ts.length <= 10 ? ts : ymd(parseISO(ts)));

const OPEN_INVOICE_STATUSES = new Set(['sent', 'partially_paid', 'overdue']);

export const ENQUIRY_STAGES = [
  { key: 'pending', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'quoted', label: 'Quoted' },
  { key: 'converted', label: 'Booked' },
  { key: 'declined', label: 'Declined' },
] as const;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function computeInsights(input: InsightInput, window: InsightWindow, today: Date = new Date()): Insights {
  const { start, end, prevStart, prevEnd } = window;
  const price = new Map(input.services.map((s) => [s.id, Number.isFinite(s.price) ? s.price : 0]));
  const priceOf = (j: InsightJob) => (j.service_id ? price.get(j.service_id) ?? 0 : 0);
  const live = (j: InsightJob) => j.status !== 'cancelled';

  const cur = input.jobs.filter((j) => inRange(j.scheduled_date, start, end));
  const prev = input.jobs.filter((j) => inRange(j.scheduled_date, prevStart, prevEnd));
  const curLive = cur.filter(live);
  const prevLive = prev.filter(live);
  const sum = (jobs: InsightJob[]) => jobs.reduce((t, j) => t + priceOf(j), 0);

  const revenue = compare(sum(curLive), sum(prevLive));
  const jobs = compare(curLive.length, prevLive.length);
  const completed = compare(
    cur.filter((j) => j.status === 'completed').length,
    prev.filter((j) => j.status === 'completed').length,
  );
  const avgJobValue = compare(
    curLive.length ? revenue.current / curLive.length : 0,
    prevLive.length ? revenue.previous / prevLive.length : 0,
  );

  const countIn = (rows: InsightDated[], s: string, e: string) => rows.filter((r) => inRange(dayOf(r.created_at), s, e)).length;
  const newCustomers = compare(countIn(input.customers, start, end), countIn(input.customers, prevStart, prevEnd));

  const leads = input.enquiries.filter(countsAsLead);
  const curEnq = leads.filter((e) => inRange(dayOf(e.created_at), start, end));
  const prevEnq = leads.filter((e) => inRange(dayOf(e.created_at), prevStart, prevEnd));
  const enquiries = compare(curEnq.length, prevEnq.length);
  const rate = (rows: InsightEnquiry[]) =>
    rows.length ? (rows.filter((e) => e.status === 'converted').length / rows.length) * 100 : 0;
  const conversion = compare(rate(curEnq), rate(prevEnq));

  // Repeat customers: identify by customer id, else email, else name.
  const who = (j: InsightJob) =>
    j.customer_id || j.client_email?.trim().toLowerCase() || j.client_name?.trim().toLowerCase() || null;
  const repeatFor = (jobsInPeriod: InsightJob[], before: InsightJob[]) => {
    const counts = new Map<string, number>();
    jobsInPeriod.forEach((j) => {
      const k = who(j);
      if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
    });
    if (!counts.size) return 0;
    const seenBefore = new Set(before.map(who).filter(Boolean) as string[]);
    let repeat = 0;
    counts.forEach((n, k) => {
      if (n > 1 || seenBefore.has(k)) repeat += 1;
    });
    return (repeat / counts.size) * 100;
  };
  const repeatRate = compare(repeatFor(curLive, prevLive), repeatFor(prevLive, []));

  const series = window.buckets.map((b) => {
    const c = curLive.filter((j) => inRange(j.scheduled_date, b.start, b.end));
    const p = prevLive.filter((j) => inRange(j.scheduled_date, b.prevStart, b.prevEnd));
    return {
      label: b.label,
      tooltip: b.tooltip,
      prevTooltip: b.prevTooltip,
      revenue: sum(c),
      prevRevenue: sum(p),
      jobs: c.length,
      prevJobs: p.length,
    };
  });

  const cancelled = cur.length - curLive.length;
  const outcomes: Insights['outcomes'] = [
    { key: 'completed', label: 'Completed', value: completed.current },
    { key: 'open', label: 'Booked / not closed', value: curLive.length - completed.current },
    { key: 'cancelled', label: 'Cancelled', value: cancelled },
  ];

  const serviceName = new Map(input.services.map((s) => [s.id, s.name]));
  const svcAgg = new Map<string, { id: string; name: string; jobs: number; revenue: number }>();
  curLive.forEach((j) => {
    const id = j.service_id ?? 'none';
    const row = svcAgg.get(id) ?? { id, name: serviceName.get(id) ?? 'Other', jobs: 0, revenue: 0 };
    row.jobs += 1;
    row.revenue += priceOf(j);
    svcAgg.set(id, row);
  });
  const byService = [...svcAgg.values()].sort((a, b) => b.revenue - a.revenue || b.jobs - a.jobs);

  const byWeekday = WEEKDAYS.map((day) => ({ day, jobs: 0 }));
  curLive.forEach((j) => {
    const dow = parseISO(j.scheduled_date).getDay(); // 0 = Sun
    byWeekday[(dow + 6) % 7].jobs += 1;
  });

  const team = input.members
    .map((m) => {
      const mine = curLive.filter((j) => j.team_member_id === m.id);
      return {
        id: m.id,
        name: m.name,
        completed: mine.filter((j) => j.status === 'completed').length,
        jobs: mine.length,
        revenue: sum(mine),
      };
    })
    .filter((m) => m.jobs > 0)
    .sort((a, b) => b.completed - a.completed || b.revenue - a.revenue);

  const stageCounts = (rows: InsightEnquiry[]) => {
    const counts: Record<string, number> = {};
    ENQUIRY_STAGES.forEach((s) => (counts[s.key] = 0));
    rows.forEach((e) => {
      const key = e.status in counts ? e.status : 'pending';
      counts[key] += 1;
    });
    return counts;
  };
  const allCounts = stageCounts(curEnq);
  const enqBySvc = new Map<string, InsightEnquiry[]>();
  curEnq.forEach((e) => {
    const id = e.service_id ?? 'none';
    enqBySvc.set(id, [...(enqBySvc.get(id) ?? []), e]);
  });
  const pipeline = {
    total: curEnq.length,
    stages: ENQUIRY_STAGES.map((s) => ({ key: s.key, label: s.label, value: allCounts[s.key] })),
    byService: [...enqBySvc.entries()]
      .map(([id, rows]) => ({
        id,
        name: serviceName.get(id) ?? 'General enquiry',
        total: rows.length,
        counts: stageCounts(rows),
      }))
      .sort((a, b) => b.total - a.total),
  };

  const bySource = LEAD_SOURCES.map((src) => {
    const rows = curEnq.filter((e) => leadSourceOf(e.lead_source) === src.value);
    const converted = rows.filter((e) => e.status === 'converted').length;
    return {
      key: src.value as string,
      label: src.label as string,
      total: rows.length,
      converted,
      rate: rows.length ? (converted / rows.length) * 100 : 0,
    };
  })
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total || b.rate - a.rate);

  const todayStr = ymd(today);
  const agingBuckets = [
    { key: 'current', label: 'Not due yet', amount: 0, count: 0 },
    { key: '1-30', label: '1–30 days overdue', amount: 0, count: 0 },
    { key: '31-60', label: '31–60 days overdue', amount: 0, count: 0 },
    { key: '60+', label: 'Over 60 days overdue', amount: 0, count: 0 },
  ];
  input.invoices.forEach((inv) => {
    if (!OPEN_INVOICE_STATUSES.has(inv.status)) return;
    const balance = Number(inv.total_amount ?? 0) - Number(inv.amount_paid ?? 0);
    if (!(balance > 0)) return;
    let idx = 0;
    if (inv.due_date && inv.due_date < todayStr) {
      const days = Math.round((parseISO(todayStr).getTime() - parseISO(inv.due_date).getTime()) / 86_400_000);
      idx = days <= 30 ? 1 : days <= 60 ? 2 : 3;
    } else if (!inv.due_date && inv.status === 'overdue') {
      idx = 1;
    }
    agingBuckets[idx].amount += balance;
    agingBuckets[idx].count += 1;
  });

  return {
    revenue,
    jobs,
    completed,
    avgJobValue,
    newCustomers,
    enquiries,
    conversion,
    repeatRate,
    series,
    outcomes,
    byService,
    byWeekday,
    team,
    pipeline,
    bySource,
    outstanding: {
      total: agingBuckets.reduce((t, b) => t + b.amount, 0),
      count: agingBuckets.reduce((t, b) => t + b.count, 0),
      buckets: agingBuckets,
    },
  };
}
